import { useEffect, useState } from 'react';

export function readPreference<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(`hearthhop:${key}`);
    return stored === null ? fallback : JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

export function usePreference<T>(key: string, fallback: T) {
  const [value, setValue] = useState<T>(() => readPreference(key, fallback));
  useEffect(() => {
    try {
      localStorage.setItem(`hearthhop:${key}`, JSON.stringify(value));
    } catch {
      // Preferences are optional; private browsing must still be playable.
    }
  }, [key, value]);
  return [value, setValue] as const;
}

export function useReducedMotion() {
  const [systemPreference, setSystemPreference] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const [override, setOverride] = usePreference<boolean | null>('reduced-motion', null);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setSystemPreference(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return [override ?? systemPreference, setOverride] as const;
}

export function parseRoomCode(value: string): string | null {
  const trimmed = value.trim();
  if (/^[a-z0-9]{6}$/i.test(trimmed)) return trimmed.toUpperCase();
  try {
    const url = new URL(trimmed, window.location.origin);
    const code = url.searchParams.get('room')?.trim();
    return code && /^[a-z0-9]{6}$/i.test(code) ? code.toUpperCase() : null;
  } catch {
    return null;
  }
}

export function inviteURL(code: string): string {
  const url = new URL('/', window.location.origin);
  url.searchParams.set('room', code);
  return url.toString();
}

export function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  return `${minutes.toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
}
